import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../lib/firebase";
import * as XLSX from "xlsx";
import CentralSupplyPage from "./CentralSupplyPage";
import RequisitionBuilderPage from "./RequisitionBuilderPage";
import KitBuilderPage from "./KitBuilderPage";

const STATUS = {
  pending: { label: "Pending", color: "#dc2626", bg: "#fee2e2", border: "#fecaca" },
  complete: { label: "Complete", color: "#15803d", bg: "#dcfce7", border: "#bbf7d0" },
  actionRequired: { label: "Action Required", color: "#a16207", bg: "#fef3c7", border: "#fde68a" },
  inProgress: {
  label: "In Progress",
  color: "#2563eb",
  bg: "#dbeafe",
  border: "#bfdbfe",
},
oos: {
  label: "OOS",
  color: "#374151",
  bg: "#e5e7eb",
  border: "#d1d5db",
},
};

const DASHBOARD_LOCATIONS = [
  { id: "cart3", name: "Cart 3", type: "cart" },
  { id: "cart5", name: "Cart 5", type: "cart" },
  { id: "cart9", name: "Cart 9", type: "cart" },
  { id: "cart12", name: "Cart 12", type: "cart" },
  { id: "cart15", name: "Cart 15", type: "cart" },
  { id: "cart17", name: "Cart 17", type: "cart" },
  { id: "mdcart", name: "MD Cart", type: "cart" },
  { id: "bike1", name: "Bike 1", type: "bike" },
  { id: "bike2", name: "Bike 2", type: "bike" },
  { id: "bike3", name: "Bike 3", type: "bike" },
  { id: "tent3", name: "Tent 3", type: "tent" },
  { id: "tent5", name: "Tent 5", type: "tent" },
  { id: "tent9", name: "Tent 9", type: "tent" },
  { id: "tent12", name: "Tent 12", type: "tent" },
  { id: "tent17", name: "Tent 17", type: "tent" },
  { id: "medicalhq", name: "Medical HQ", type: "hq" },
];

function locationNameFromId(locationId = "") {
  return (
    DASHBOARD_LOCATIONS.find((loc) => loc.id === locationId)?.name ||
    locationId
  );
}

const SOURCE_LOCATIONS = [
  { id: "onsiteStock", label: "On Site Supply" },
  { id: "centralSupply", label: "Central Supply" },
];

function getTodayKey() {
  return new Date().toISOString().slice(0, 10);
}

function groupLabel(type) {
  if (type === "cart") return "Carts";
  if (type === "bike") return "Bikes";
  if (type === "tent") return "Tents";
  if (type === "hq") return "HQ";
  if (type === "centralSupply" || type === "onsiteStock") return "Supply";
  return "Other";
}

function normalizeLots(item = {}) {
  if (Array.isArray(item.lots) && item.lots.length > 0) {
    return item.lots.map((lot) => ({
      expirationDate: lot.expirationDate || "",
      quantity: Number(lot.quantity || 0),
    }));
  }

  return [
    {
      expirationDate: item.expirationDate || "",
      quantity: Number(item.quantityOnHand || 0),
    },
  ];
}

function lotTotal(lots = []) {
  return lots.reduce((sum, lot) => sum + Number(lot.quantity || 0), 0);
}

function subtractLots(lots = [], qtyToRemove = 0) {
  let remaining = Number(qtyToRemove || 0);
  const issuedLots = [];

  const sorted = [...lots].sort((a, b) =>
    String(a.expirationDate || "9999-12-31").localeCompare(
      String(b.expirationDate || "9999-12-31")
    )
  );

  const updatedLots = sorted
    .map((lot) => {
      const qty = Number(lot.quantity || 0);
      if (remaining <= 0) return lot;

      const take = Math.min(qty, remaining);
      remaining -= take;

      if (take > 0) {
        issuedLots.push({
          expirationDate: lot.expirationDate || "",
          quantity: take,
        });
      }

      return {
        ...lot,
        quantity: qty - take,
      };
    })
    .filter((lot) => Number(lot.quantity || 0) > 0);

  return { updatedLots, issuedLots, unfilledQty: remaining };
}

function addLots(existingLots = [], incomingLots = []) {
  const map = {};

  [...existingLots, ...incomingLots].forEach((lot) => {
    const key = lot.expirationDate || "";
    if (!map[key]) {
      map[key] = { expirationDate: key, quantity: 0 };
    }
    map[key].quantity += Number(lot.quantity || 0);
  });

  return Object.values(map).filter((lot) => Number(lot.quantity || 0) > 0);
}

function daysUntil(dateString) {
  if (!dateString) return null;

  const today = new Date(getTodayKey());
  const exp = new Date(dateString);
  const diff = exp.getTime() - today.getTime();

  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function getItemStatus(item) {
  const par = Number(item.parLevel || 0);
  const current = Number(item.currentQty ?? item.countedQty ?? 0);
  const expDays = daysUntil(item.expirationDate);

  if (expDays !== null && expDays < 0) return "expired";
  if (current < par) return "below_par";
  return "at_par";
}

function getActionItemsFromInventory(items = [], parentKitName = "") {
  return items.flatMap((item) => {
    // Component / nested kit: do NOT return the component itself.
    // Instead, look inside it and return only actual missing/expired supplies.
    if (item.type === "nestedKit") {
      return getActionItemsFromInventory(
        item.nestedItems || [],
        item.itemName || parentKitName
      );
    }

    const wasChecked =
      item.checkStatus &&
      item.checkStatus !== "unchecked";

    const isMissing =
      Number(item.missingQty || 0) > 0;

    const isExpired =
      wasChecked && getItemStatus(item) === "expired";

    if (!wasChecked || (!isMissing && !isExpired)) {
      return [];
    }

    return [
      {
        ...item,
        parentKitName,
        kitLocationName: parentKitName
          ? `${parentKitName} Kit`
          : item.kitLocationName || "Kit",
      },
    ];
  });
}

export default function AssetManagementPage() {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [dailyInventories, setDailyInventories] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [selectedLocation, setSelectedLocation] = useState(null);

  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [issueItems, setIssueItems] = useState([]);
  const [issueInventory, setIssueInventory] = useState(null);

  const [stockItems, setStockItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [addItemModalOpen, setAddItemModalOpen] = useState(false);
 const [editStockItem, setEditStockItem] = useState(null);
const [adjustStockItem, setAdjustStockItem] = useState(null);
const [categoryModalOpen, setCategoryModalOpen] = useState(false);
 

  const [stockSearch, setStockSearch] = useState("");
  const [stockCategoryFilter, setStockCategoryFilter] = useState("all");
  const [stockLocationFilter, setStockLocationFilter] = useState("all");
  const [stockSort, setStockSort] = useState("az");

  const [importing, setImporting] = useState(false);

  useEffect(() => {
    loadLocations();
  }, []);

  async function loadLocations() {
    try {
      setLoading(true);

      const today = getTodayKey();

     const [
  locSnap,
  stockSnap,
  invSnap,
  requestSnap,
  categorySnap,
] = await Promise.all([
  getDocs(collection(db, "inventoryLocations")),
  getDocs(collection(db, "inventoryStock")),
  getDocs(collection(db, "dailyInventories")),
  getDocs(collection(db, "supplyRequests")),
  getDocs(collection(db, "inventoryCategories")),
]);

      const stockData = stockSnap.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        .filter((item) => item.active !== false)
        .map((item) => {
          const lots = normalizeLots(item);
          return {
            ...item,
            lots,
            quantityOnHand: lotTotal(lots),
          };
        });

      setStockItems(stockData);
      const categoryData = categorySnap.docs
  .map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  }))
  .filter((cat) => cat.active !== false)
  .sort((a, b) =>
    String(a.name || "").localeCompare(String(b.name || ""))
  );

setCategories(categoryData);

      const inventoryMap = {};
      invSnap.docs.forEach((docSnap) => {
        const data = { id: docSnap.id, ...docSnap.data() };
        if (data.inventoryDate === today) {
          inventoryMap[data.locationId] = data;
        }
      });

      setDailyInventories(inventoryMap);

      const requestMap = {};
const requestItemsMap = {};

requestSnap.docs.forEach((docSnap) => {
  const data = {
    id: docSnap.id,
    ...docSnap.data(),
  };

  if (data.status === "pending") {
    requestMap[data.locationId] =
      (requestMap[data.locationId] || 0) + 1;

    if (!requestItemsMap[data.locationId]) {
      requestItemsMap[data.locationId] = [];
    }

    requestItemsMap[data.locationId].push(data);
  }
});

const data = DASHBOARD_LOCATIONS.map((loc) => {
  const daily = inventoryMap[loc.id];

 let status = "pending";

if (daily?.outOfService === true) {
  status = "oos";
} else if (daily) {
  const needsAction =
    daily.actionRequired === true ||
    getActionItemsFromInventory(daily.items || []).length > 0;

  const hasRequests =
    (requestMap[loc.id] || 0) > 0;

  if (daily.status === "in_progress") {
    status = "inProgress";
  } else if (needsAction || hasRequests) {
    status = "actionRequired";
  } else {
    status = "complete";
  }
}

  return {
    ...loc,
    status,
    dailyInventory: daily || null,
    group: groupLabel(loc.type),
    openRequests: requestMap[loc.id] || 0,
    pendingRequests: requestItemsMap[loc.id] || [],
  };
});

      setLocations(data);
    } catch (err) {
      console.error("Failed to load inventory locations:", err);
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteStockItem(item) {
    const confirmed = window.confirm(`Remove ${item.itemName} from inventory?`);
    if (!confirmed) return;

    try {
      await updateDoc(doc(db, "inventoryStock", item.id), {
        active: false,
        updatedAt: serverTimestamp(),
      });

      await loadLocations();
    } catch (err) {
      console.error("Failed to delete inventory item:", err);
      alert("Failed to remove item.");
    }
  }

async function handlePrintOpenWorkOrders() {
  try {
    const { jsPDF } = await import("jspdf");

    const requestSnap = await getDocs(
      query(
        collection(db, "supplyRequests"),
        where("status", "==", "pending")
      )
    );

    const grouped = {};

    requestSnap.docs.forEach((docSnap) => {
      const request = { id: docSnap.id, ...docSnap.data() };
      const locationId = request.locationId || "unknown";
      const locationName =
        request.locationName || locationNameFromId(locationId);

      if (!grouped[locationId]) {
        grouped[locationId] = {
          locationName,
          items: [],
        };
      }

      (request.items || []).forEach((item) => {
        grouped[locationId].items.push({
          ...item,
          notes: request.notes || "",
        });
      });
    });

    const units = Object.values(grouped).sort((a, b) =>
      String(a.locationName || "").localeCompare(String(b.locationName || ""))
    );

    if (units.length === 0) {
      alert("There are no open work orders.");
      return;
    }

    const pdf = new jsPDF("p", "pt", "letter");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    let y = 54;

    function addText(text, x, size = 11, style = "normal") {
      pdf.setFont("helvetica", style);
      pdf.setFontSize(size);
      pdf.text(String(text || ""), x, y);
    }

    function checkPageSpace(spaceNeeded = 60) {
      if (y + spaceNeeded > pageHeight - 54) {
        pdf.addPage();
        y = 54;
      }
    }

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(18);
    pdf.text("Open Supply Work Orders", 40, y);

    y += 18;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.text(`Generated: ${new Date().toLocaleString()}`, 40, y);

    y += 30;

    units.forEach((unit) => {
      checkPageSpace(90);

      pdf.setFillColor(15, 23, 42);
      pdf.rect(40, y, pageWidth - 80, 24, "F");

      pdf.setTextColor(255, 255, 255);
pdf.setFont("helvetica", "bold");
pdf.setFontSize(13);
pdf.text(unit.locationName || "Unknown Unit", 50, y + 16);

pdf.setTextColor(15, 23, 42);
y += 38;

      const groupedBySection = unit.items.reduce((acc, item) => {
        const section = item.kitLocationName || "Other";
        if (!acc[section]) acc[section] = [];
        acc[section].push(item);
        return acc;
      }, {});

      Object.entries(groupedBySection).forEach(([sectionName, sectionItems]) => {
        checkPageSpace(50);

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(11);
        pdf.text(sectionName, 50, y);

        y += 16;

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(9);
        pdf.text("Item", 60, y);
        pdf.text("PAR", 330, y);
        pdf.text("Need", 380, y);
        pdf.text("Unit", 435, y);

        y += 8;

        pdf.setDrawColor(203, 213, 225);
        pdf.line(60, y, pageWidth - 60, y);

        y += 16;

        sectionItems
          .sort((a, b) =>
            String(a.itemName || "").localeCompare(String(b.itemName || ""))
          )
          .forEach((item) => {
            checkPageSpace(35);

            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(10);

            const itemName = String(item.itemName || "");
            const wrappedName = pdf.splitTextToSize(itemName, 250);

            pdf.text(wrappedName, 60, y);
            pdf.text(String(item.parLevel || 0), 330, y);
            pdf.text(String(item.qtyRequested || 0), 385, y);
            pdf.text(String(item.unitType || "each"), 435, y);

            y += Math.max(18, wrappedName.length * 12);

            if (item.notes) {
              pdf.setFont("helvetica", "italic");
              pdf.setFontSize(9);
              const noteText = pdf.splitTextToSize(`Note: ${item.notes}`, 420);
              pdf.text(noteText, 75, y);
              y += noteText.length * 11 + 4;
            }
          });

        y += 12;
      });

      y += 10;
    });

    pdf.save("open-supply-work-orders.pdf");
  } catch (err) {
    console.error("Failed to create work order PDF:", err);
    alert("Failed to create work order PDF.");
  }
}

  async function handleImportSpreadsheet(event) {
    try {
      setImporting(true);

      const file = event.target.files[0];
      if (!file) return;

      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const allRows = [];

      workbook.SheetNames.forEach((sheetName) => {
        const worksheet = workbook.Sheets[sheetName];

        const rows = XLSX.utils.sheet_to_json(worksheet, {
          defval: "",
          range: 1,
        });

        rows.forEach((row) => {
          const itemName =
            row["Medical Supplies"] ||
            row["Medication"] ||
            row["Medications"] ||
            row["Item"] ||
            row["Item Name"] ||
            "";

          if (!String(itemName).trim()) return;

          const qty = Number(row["Qty. Required"] || 0);
          const exp = String(row["Expiration Date"] || "").trim();

          allRows.push({
            itemName: String(itemName).trim(),
            category: sheetName.toLowerCase().includes("med")
              ? "Medication"
              : "Medical Supply",
            unitType: String(row["Unit"] || "each").trim().toLowerCase(),
            orderPackType: "each",
            unitsPerPack: 1,
            peoplesoftNumber: String(row["Peoplesoft #"] || "").trim(),
            psMfgNumber: String(row["PS Mfg No."] || "").trim(),
            manufacturerNumber: String(row["Mfg #"] || "").trim(),
            vendorPartNumber: String(row["Vendor part #"] || "").trim(),
            supplier: String(row["Vendor"] || "").trim(),
            parLevel: qty,
            reorderLevel: 0,
            quantityOnHand: 0,
            lots: [{ expirationDate: exp, quantity: 0 }],
            locationId: "onsiteStock",
            shelfBin: "",
            expirationDate: exp || null,
            price: null,
            active: true,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        });
      });

      for (const item of allRows) {
        await addDoc(collection(db, "inventoryStock"), item);
      }

      alert(`Successfully imported ${allRows.length} inventory items.`);
      await loadLocations();

      if (event?.target) event.target.value = "";
    } catch (err) {
      console.error("Import failed:", err);
      alert(`Import failed: ${err?.message || "Unknown error"}`);
    } finally {
      setImporting(false);
    }
  }

  const counts = useMemo(() => {
    return locations.reduce(
      (acc, loc) => {
        acc[loc.status] += 1;
        return acc;
      },
      { pending: 0, complete: 0, actionRequired: 0 }
    );
  }, [locations]);

  const groupedLocations = useMemo(() => {
    const order = ["Carts", "Bikes", "Tents", "HQ", "Supply", "Other"];

    const groups = locations.reduce((acc, loc) => {
      if (!acc[loc.group]) acc[loc.group] = [];
      acc[loc.group].push(loc);
      return acc;
    }, {});

    return order
      .filter((group) => groups[group])
      .map((group) => ({
        group,
        locations: groups[group],
      }));
  }, [locations]);

  if (loading) {
    return <div style={styles.page}>Loading Asset Management...</div>;
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Asset Management</h1>
          <button
  style={styles.returnButton}
  onClick={() => navigate("/admin")}
>
  ← Return to Admin Portal
</button>

          <div style={styles.tabs}>
            {[
              ["dashboard", "Dashboard"],
              ["kitBuilder", "Kit Builder"],
              ["centralSupply", "Inventory Management"],
              ["requisitionBuilder", "Requisition Builder"],
            ].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                style={{
                  ...styles.tab,
                  ...(activeTab === key ? styles.tabActive : {}),
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div style={styles.summaryGrid}>
          <SummaryBox label="Pending" count={counts.pending} status="pending" />
          <SummaryBox label="Complete" count={counts.complete} status="complete" />
          <SummaryBox
            label="Action"
            count={counts.actionRequired}
            status="actionRequired"
          />
        </div>
      </div>

      {activeTab === "dashboard" && (
  <div style={styles.dashboard}>
    <div style={styles.dashboardActions}>
      <button
        style={styles.primaryButton}
        onClick={handlePrintOpenWorkOrders}
      >
        Print Open Work Orders
      </button>
    </div>
    {groupedLocations.map(({ group, locations }) => (
      <section key={group} style={styles.section}>
        <h2 style={styles.sectionTitle}>{group}</h2>

        <div style={styles.dashboardTable}>
          <div style={styles.dashboardHeaderRow}>
            <div>Unit</div>
            <div>Inventory</div>
            <div>Below PAR</div>
            <div>Expired</div>
            <div>Requests</div>
            <div>Status</div>
            <div>Actions</div>
          </div>

          {locations.map((location) => (
            <LocationRow
              key={location.id}
              location={location}
              onClick={() => setSelectedLocation(location)}
            />
          ))}
        </div>
      </section>
    ))}
  </div>
)}

      {activeTab === "kitBuilder" && (
        <KitBuilderPage stockItems={stockItems} styles={styles} />
      )}

      {activeTab === "centralSupply" && (
        <CentralSupplyPage
          styles={styles}
          stockSearch={stockSearch}
          setStockSearch={setStockSearch}
          categoryFilter={stockCategoryFilter}
          setCategoryFilter={setStockCategoryFilter}
          locationFilter={stockLocationFilter}
          setLocationFilter={setStockLocationFilter}
          sortMode={stockSort}
          setSortMode={setStockSort}
          stockItems={stockItems}
          stockCategories={categories.map((c) => c.name)}
          onManageCategories={() => setCategoryModalOpen(true)}
          onAddItem={() => setAddItemModalOpen(true)}
          onReceiveStock={() => setReceiveModalOpen(true)}
          onImportSpreadsheet={handleImportSpreadsheet}
          importing={importing}
          onEditItem={setEditStockItem}
          onAdjustQuantity={setAdjustStockItem}
          onDeleteItem={handleDeleteStockItem}
        />
      )}

      {activeTab === "requisitionBuilder" && (
        <RequisitionBuilderPage stockItems={stockItems} styles={styles} />
      )}

      {selectedLocation && (
        <LocationModal
          location={selectedLocation}
          onClose={() => setSelectedLocation(null)}
          onInventorySaved={loadLocations}
          onIssue={(items, inventory) => {
          setIssueItems(items);
          setIssueInventory(inventory);
          setSelectedLocation(null);
          setIssueModalOpen(true);
        }}
        />
      )}

      {issueModalOpen && (
        <IssueItemsModal
          items={issueItems}
          inventory={issueInventory}
          onIssueComplete={loadLocations}
          onClose={() => {
            setIssueModalOpen(false);
            setIssueItems([]);
            setIssueInventory(null);
          }}
        />
      )}

      {receiveModalOpen && (
        <ReceiveStockModal
          stockItems={stockItems}
          onClose={() => setReceiveModalOpen(false)}
          onReceiveComplete={loadLocations}
        />
      )}

      {categoryModalOpen && (
  <ManageCategoriesModal
    categories={categories}
    stockItems={stockItems}
    onClose={() => setCategoryModalOpen(false)}
    onSaved={loadLocations}
  />
)}

      {addItemModalOpen && (
        <AddInventoryItemModal
  categories={categories}
  onClose={() => setAddItemModalOpen(false)}
  onAddComplete={loadLocations}
/>
      )}

      {editStockItem && (
       <EditInventoryItemModal
  item={editStockItem}
  categories={categories}
  onClose={() => setEditStockItem(null)}
  onSaveComplete={loadLocations}
/>
      )}

      {adjustStockItem && (
  <QuickAdjustQuantityModal
    item={adjustStockItem}
    onClose={() => setAdjustStockItem(null)}
    onSaved={loadLocations}
  />
)}
    </div>
  );
}

function SummaryBox({ label, count, status }) {
  const config = STATUS[status];

  return (
    <div
      style={{
        ...styles.summaryBox,
        background: config.bg,
        borderColor: config.border,
        color: config.color,
      }}
    >
      <div style={styles.summaryCount}>{count}</div>
      <div style={styles.summaryLabel}>{label}</div>
    </div>
  );
}

function LocationRow({ location, onClick }) {
  const config = STATUS[location.status];
  const daily = location.dailyInventory;

  const expiredCount =
    daily?.items?.filter(
      (item) => getItemStatus(item) === "expired"
    ).length || 0;

  const belowParCount =
  getActionItemsFromInventory(daily?.items || []).filter(
    (item) => getItemStatus(item) === "below_par"
  ).length || 0;

  const openRequests = location.openRequests || 0;

  const rowBackground =
    location.status === "complete"
      ? "#dcfce7"
      : location.status === "actionRequired"
      ? "#fee2e2"
      : "#f1f5f9";

  return (
    <div
      onClick={onClick}
      style={{
        ...styles.dashboardRow,
        background: rowBackground,
      }}
    >
      <div>
        <strong>{location.name || location.id}</strong>

        <div
          style={{
            fontSize: 12,
            color: "#64748b",
            marginTop: 2,
          }}
        >
          {location.type}
        </div>
      </div>

      <div>
        {daily?.status === "submitted"
          ? "Submitted"
          : "Due"}
      </div>

      <div>{belowParCount}</div>

      <div>{expiredCount}</div>

      <div>{openRequests}</div>

      <div>
        <span
          style={{
            ...styles.badge,
            background: config.bg,
            borderColor: config.border,
            color: config.color,
          }}
        >
          {config.label}
        </span>
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <button
          style={styles.smallButton}
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
        >
          View
        </button>

        {(belowParCount > 0 ||
          expiredCount > 0 ||
          openRequests > 0) && (
          <button
            style={styles.smallDangerButton}
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
          >
            Action Needed
          </button>
        )}
      </div>
    </div>
  );
}

function LocationModal({ location, onClose, onIssue, onInventorySaved }) {
  const config = STATUS[location.status];

  const [kitTemplate, setKitTemplate] = useState(null);
  const [loadingKit, setLoadingKit] = useState(false);
  const [counts, setCounts] = useState({});
  const [expirationDates, setExpirationDates] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showFullInventory] = useState(false);
  const [showExpirationAlerts, setShowExpirationAlerts] = useState(false);
  const [expirationFilter, setExpirationFilter] = useState("expired");

  const dailyInventory = location.dailyInventory;

  useEffect(() => {
    async function loadAssignedKit() {
      if (!location.kitTemplateId) return;

      try {
        setLoadingKit(true);

        const snap = await getDoc(
          doc(db, "inventoryKitTemplates", location.kitTemplateId)
        );

        if (snap.exists()) {
          const template = { id: snap.id, ...snap.data() };
          setKitTemplate(template);

          if (dailyInventory?.items?.length) {
            const countMap = {};
            const expMap = {};

            dailyInventory.items.forEach((item) => {
              countMap[item.kitItemId || item.id] = Number(item.currentQty || 0);
              expMap[item.kitItemId || item.id] = item.expirationDate || "";
            });

            setCounts(countMap);
            setExpirationDates(expMap);
          }
        }
      } catch (err) {
        console.error("Failed to load assigned kit:", err);
      } finally {
        setLoadingKit(false);
      }
    }

    loadAssignedKit();
  }, [location.kitTemplateId, dailyInventory]);

  const flatKitItems = useMemo(() => {
    if (!kitTemplate?.locations) return [];

    return kitTemplate.locations.flatMap((kitLocation) =>
      (kitLocation.items || []).map((item) => ({
        ...item,
        kitLocationId: kitLocation.id,
        kitLocationName: kitLocation.name,
      }))
    );
  }, [kitTemplate]);

  const inventoryItems = useMemo(() => {
    if (dailyInventory?.items?.length) return dailyInventory.items;

    return flatKitItems.map((item) => {
      const par = Number(item.parLevel || 0);
      const current = Number(counts[item.id] || 0);
      const missing = Math.max(0, par - current);
      const exp = expirationDates[item.id] || item.expirationDate || "";

      return {
        id: item.id,
        kitItemId: item.id,
        inventoryStockId: item.inventoryStockId || "",
        itemName: item.itemName || "",
        category: item.category || "",
        unitType: item.unitType || "each",
        kitLocationName: item.kitLocationName || "",
        parLevel: par,
        countedQty: current,
        currentQty: current,
        replenishedQty: 0,
        missingQty: missing,
        expirationDate: exp,
        status: missing > 0 ? "below_par" : "at_par",
      };
    });
  }, [dailyInventory, flatKitItems, counts, expirationDates]);

  const requestActionItems = (location.pendingRequests || []).flatMap((request) =>
  (request.items || []).map((item) => ({
    ...item,
    requestId: request.id,
    requestNotes: request.notes || "",
    requestDate: request.requestDate || "",
    locationName: request.locationName || "",
    kitItemId: item.itemId,
    currentQty: 0,
    parLevel: item.parLevel || 0,
    missingQty: item.qtyRequested || 0,
  }))
);

 const dailyActionItems = getActionItemsFromInventory(inventoryItems);

const actionItems = [
  ...dailyActionItems,
  ...requestActionItems.filter((requestItem) => {
    return !dailyActionItems.some(
      (dailyItem) =>
        String(dailyItem.itemName || "") === String(requestItem.itemName || "")
    );
  }),
];

  const expirationAlertItems = inventoryItems
    .map((item) => ({
      ...item,
      daysLeft: daysUntil(item.expirationDate),
    }))
    .filter((item) => item.daysLeft !== null)
    .filter((item) => {
      if (expirationFilter === "expired") return item.daysLeft < 0;
      return item.daysLeft >= 0 && item.daysLeft <= Number(expirationFilter);
    })
    .sort((a, b) => Number(a.daysLeft || 0) - Number(b.daysLeft || 0));

  function updateCount(itemId, value) {
    setCounts((prev) => ({
      ...prev,
      [itemId]: Number(value || 0),
    }));
  }

  function updateExpiration(itemId, value) {
    setExpirationDates((prev) => ({
      ...prev,
      [itemId]: value,
    }));
  }

  async function submitInventory() {
    if (submitting) return;

    if (!kitTemplate) {
      alert("No assigned kit template found.");
      return;
    }

    try {
      setSubmitting(true);

      const today = getTodayKey();
      const inventoryId = `${location.id}_${today}`;

      const items = flatKitItems.map((item) => {
        const par = Number(item.parLevel || 0);
        const current = Number(counts[item.id] || 0);
        const missing = Math.max(0, par - current);
        const expirationDate = expirationDates[item.id] || "";

        return {
          id: item.id,
          kitItemId: item.id,
          inventoryStockId: item.inventoryStockId || "",
          itemName: item.itemName || "",
          category: item.category || "",
          unitType: item.unitType || "each",
          kitLocationName: item.kitLocationName || "",
          parLevel: par,
          countedQty: current,
          currentQty: current,
          replenishedQty: 0,
          missingQty: missing,
          expirationDate,
          status: getItemStatus({
            parLevel: par,
            currentQty: current,
            expirationDate,
          }),
        };
      });

      const needsAction = items.some((item) =>
        ["below_par", "expired"].includes(getItemStatus(item))
      );

      await setDoc(
        doc(db, "dailyInventories", inventoryId),
        {
          locationId: location.id,
          locationName: location.name || location.id,
          inventoryDate: today,
          kitTemplateId: kitTemplate.id,
          kitTemplateName: kitTemplate.name || "",
          status: "submitted",
          actionRequired: needsAction,
          items,
          crew: [],
          submittedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await onInventorySaved?.();
      alert("Inventory submitted.");
      onClose();
    } catch (err) {
      console.error("Failed to submit inventory:", err);
      alert("Failed to submit inventory.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={styles.modalOverlay}>
      <div style={{ ...styles.modal, maxWidth: 1050 }}>
        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>{location.name || location.id}</h2>
            <p style={styles.subtitle}>
              {location.kitTemplateName
                ? `Assigned Kit: ${location.kitTemplateName}`
                : "Inventory detail"}
            </p>
          </div>

          <button onClick={onClose} style={styles.closeButton}>
            Close
          </button>
        </div>

        <div style={styles.detailGrid}>
          <DetailBox label="Current Crew" value="Schedule link coming next" />

          <DetailBox
            label="Today’s Inventory"
            value={dailyInventory?.status === "submitted" ? "Submitted" : "Due"}
          />

          <DetailBox label="Status" value={config.label} color={config.color} />
        </div>

        <div style={styles.missingBox}>
          <h3>Items Below PAR</h3>

          {actionItems.length === 0 ? (
            <p>No items currently below PAR.</p>
          ) : (
            actionItems.map((item) => (
              <div key={item.kitItemId || item.id} style={styles.missingRow}>
                <span>
                  <strong>{item.itemName}</strong>
<div style={{ fontSize: 12, color: "#64748b" }}>
  {item.parentKitName
    ? `${item.parentKitName} Kit`
    : item.kitLocationName || "Kit"}{" "}
  · Current {item.currentQty || 0} / PAR {item.parLevel || 0}

  {item.requestNotes && (
    <div
      style={{
        marginTop: 4,
        color: "#0f172a",
        fontWeight: 700,
      }}
    >
      Note: {item.requestNotes}
    </div>
  )}
</div>
                </span>

                <strong>
  Need {item.missingQty || item.qtyRequested || 0}
</strong>
              </div>
            ))
          )}
        </div>

        <div style={styles.modalActions}>

          <button
            style={styles.secondaryButton}
            onClick={() => setShowExpirationAlerts((prev) => !prev)}
          >
            {showExpirationAlerts
              ? "Hide Expiration Alerts"
              : "View Expiration Alerts"}
          </button>

          <button
  style={{
    ...styles.primaryButton,
    opacity: actionItems.length === 0 ? 0.5 : 1,
  }}
  disabled={actionItems.length === 0}
  onClick={() =>
    onIssue(actionItems, {
      id: dailyInventory?.id || null,
      locationId: location.id,
      locationName: location.name || location.id,
      items: dailyInventory?.items || [],
      supplyRequestIds: [
  ...new Set([
    ...(location.pendingRequests || []).map((request) => request.id),
    ...actionItems.map((item) => item.requestId).filter(Boolean),
  ]),
],
    })
  }
>
  Issue / Replace Items
</button>
        </div>

        {showExpirationAlerts && (
          <div style={styles.missingBox}>
            <div style={styles.modalHeader}>
              <h3>Expiration Alerts</h3>

              <select
                value={expirationFilter}
                onChange={(e) => setExpirationFilter(e.target.value)}
                style={{ ...styles.select, maxWidth: 220 }}
              >
                <option value="expired">Expired</option>
                <option value="30">Expiring in 30 Days</option>
                <option value="60">Expiring in 60 Days</option>
                <option value="90">Expiring in 90 Days</option>
                <option value="365">Expiring in 365 Days</option>
              </select>
            </div>

            {expirationAlertItems.length === 0 ? (
              <p>No matching expiration alerts.</p>
            ) : (
              expirationAlertItems.map((item) => (
                <div key={`${item.kitItemId || item.id}_${item.expirationDate}`} style={styles.missingRow}>
                  <span>
                    <strong>{item.itemName}</strong>
                    <div style={{ fontSize: 12, color: "#64748b" }}>
                      {item.kitLocationName || "Kit"} · Exp:{" "}
                      {item.expirationDate || "None"}
                    </div>
                  </span>

                  <strong style={{ color: item.daysLeft < 0 ? "#dc2626" : "#a16207" }}>
                    {item.daysLeft < 0
                      ? `${Math.abs(item.daysLeft)} days expired`
                      : `${item.daysLeft} days left`}
                  </strong>
                </div>
              ))
            )}
          </div>
        )}

        {showFullInventory && (
          <div style={styles.missingBox}>
            <h3>Full Kit Inventory</h3>

            {loadingKit && <p>Loading assigned kit...</p>}

            {!loadingKit && !kitTemplate && (
              <p>No kit template could be loaded for this location.</p>
            )}

            {!loadingKit &&
              kitTemplate &&
              (kitTemplate.locations || []).map((kitLocation) => (
                <div key={kitLocation.id} style={styles.supplierGroup}>
                  <h3 style={{ marginTop: 0 }}>{kitLocation.name}</h3>

                  <div style={{ overflowX: "auto" }}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.th}>Item</th>
                          <th style={styles.th}>Category</th>
                          <th style={styles.th}>PAR</th>
                          <th style={styles.th}>Current / Counted</th>
                          <th style={styles.th}>Expiration</th>
                          <th style={styles.th}>Status</th>
                        </tr>
                      </thead>

                      <tbody>
                        {(kitLocation.items || []).map((item) => {
                          const existing = inventoryItems.find(
                            (i) => (i.kitItemId || i.id) === item.id
                          );

                          const par = Number(item.parLevel || 0);
                          const current = Number(
                            counts[item.id] ?? existing?.currentQty ?? 0
                          );
                          const exp =
                            expirationDates[item.id] ??
                            existing?.expirationDate ??
                            "";
                          const status = getItemStatus({
                            parLevel: par,
                            currentQty: current,
                            expirationDate: exp,
                          });

                          const statusConfig =
                            status === "at_par"
                              ? { label: "At PAR", color: "#15803d", bg: "#dcfce7" }
                              : status === "expired"
                              ? { label: "Expired", color: "#dc2626", bg: "#fee2e2" }
                              : { label: "Below PAR", color: "#dc2626", bg: "#fee2e2" };

                          return (
                            <tr key={item.id} style={{ background: statusConfig.bg }}>
                              <td style={styles.td}>
                                <strong>{item.itemName}</strong>
                              </td>
                              <td style={styles.td}>{item.category || "—"}</td>
                              <td style={styles.td}>{par}</td>
                              <td style={styles.td}>
                                <input
                                  type="number"
                                  min="0"
                                  value={current}
                                  onChange={(e) =>
                                    updateCount(item.id, e.target.value)
                                  }
                                  style={styles.qtyInput}
                                />
                              </td>
                              <td style={styles.td}>
                                <input
                                  type="date"
                                  value={exp || ""}
                                  onChange={(e) =>
                                    updateExpiration(item.id, e.target.value)
                                  }
                                  style={{ ...styles.input, minWidth: 150 }}
                                />
                              </td>
                              <td style={styles.td}>
                                <strong style={{ color: statusConfig.color }}>
                                  {statusConfig.label}
                                </strong>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}

            <div style={styles.modalActions}>
              <button
                onClick={submitInventory}
                disabled={submitting}
                style={{
                  ...styles.primaryButton,
                  opacity: submitting ? 0.6 : 1,
                }}
              >
                {submitting ? "Submitting..." : "Submit Inventory"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DetailBox({ label, value, color }) {
  return (
    <div style={styles.detailBox}>
      <div style={styles.detailLabel}>{label}</div>
      <div style={{ ...styles.detailValue, color: color || "#0f172a" }}>
        {value}
      </div>
    </div>
  );
}

function IssueItemsModal({ items, inventory, onClose, onIssueComplete }) {
  const [selectedItems, setSelectedItems] = useState(
    items.map((item) => ({
      ...item,
      checked: true,
      issueQty: item.missingQty || item.qty || 0,
    }))
  );

  const [saving, setSaving] = useState(false);
  const [sourceLocationId, setSourceLocationId] = useState("onsiteStock");

  function toggleItem(itemName) {
    setSelectedItems((prev) =>
      prev.map((item) =>
        item.itemName === itemName ? { ...item, checked: !item.checked } : item
      )
    );
  }

  function updateQty(itemName, qty) {
    setSelectedItems((prev) =>
      prev.map((item) =>
        item.itemName === itemName
          ? { ...item, issueQty: Number(qty || 0) }
          : item
      )
    );
  }

  async function findStockItem(locationId, itemName) {
    const stockQuery = query(
      collection(db, "inventoryStock"),
      where("locationId", "==", locationId),
      where("itemName", "==", itemName)
    );

    const snap = await getDocs(stockQuery);
    return snap.empty ? null : snap.docs[0];
  }

  async function handleIssue() {
    if (saving) return;

    try {
      setSaving(true);

      const issuedItems = selectedItems.filter(
        (item) => item.checked && Number(item.issueQty || 0) > 0
      );

      let updatedInventoryItems = [...(inventory.items || [])];
      const skippedItems = [];
      const remainingRequestItems = [];

      for (const item of issuedItems) {
        const issueQty = Number(item.issueQty || 0);

        const sourceDoc = await findStockItem(sourceLocationId, item.itemName);
        if (!sourceDoc) {
          alert(`No source stock found for ${item.itemName}.`);
          setSaving(false);
          return;
        }

        const sourceData = sourceDoc.data();
        const sourceLots = normalizeLots(sourceData);
        const sourceTotal = lotTotal(sourceLots);

        const qtyToIssue = Math.min(sourceTotal, issueQty);

        if (qtyToIssue <= 0) {
  skippedItems.push(
    `${item.itemName} - requested ${issueQty}, available 0`
  );

  remainingRequestItems.push({
    itemId: item.kitItemId || item.id,
    inventoryStockId: item.inventoryStockId || "",
    itemName: item.itemName || "",
    category: item.category || "",
    unitType: item.unitType || "each",
    kitLocationName: item.kitLocationName || "Other",
    parLevel: Number(item.parLevel || 0),
    qtyRequested: issueQty,
  });

  continue;
}

        if (qtyToIssue < issueQty) {
  const stillNeeded = issueQty - qtyToIssue;

  skippedItems.push(
    `${item.itemName} - requested ${issueQty}, issued ${qtyToIssue}, still needed ${stillNeeded}`
  );

  remainingRequestItems.push({
    itemId: item.kitItemId || item.id,
    inventoryStockId: item.inventoryStockId || "",
    itemName: item.itemName || "",
    category: item.category || "",
    unitType: item.unitType || "each",
    kitLocationName: item.kitLocationName || "Other",
    parLevel: Number(item.parLevel || 0),
    qtyRequested: stillNeeded,
  });
}

        const { updatedLots, issuedLots } = subtractLots(sourceLots, qtyToIssue);
        await updateDoc(doc(db, "inventoryStock", sourceDoc.id), {
  lots: updatedLots,
  quantityOnHand: lotTotal(updatedLots),
  updatedAt: serverTimestamp(),
});


        const destinationDoc = await findStockItem(inventory.locationId, item.itemName);

        if (destinationDoc) {
          const destinationData = destinationDoc.data();
          const destinationLots = normalizeLots(destinationData);
          const newLots = addLots(destinationLots, issuedLots);

          await updateDoc(doc(db, "inventoryStock", destinationDoc.id), {
            lots: newLots,
            quantityOnHand: lotTotal(newLots),
            updatedAt: serverTimestamp(),
          });
        } else {
          await addDoc(collection(db, "inventoryStock"), {
            locationId: inventory.locationId,
            itemName: item.itemName,
            category: item.category || "",
            unitType: item.unitType || "each",
            lots: issuedLots,
            quantityOnHand: lotTotal(issuedLots),
            active: true,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        }

        updatedInventoryItems = updatedInventoryItems.map((invItem) => {
  const issuedId = item.kitItemId || item.id;
  const topId = invItem.kitItemId || invItem.id;

  // Normal top-level item
  if (topId === issuedId) {
    const currentQty = Number(invItem.currentQty || 0) + qtyToIssue;
    const par = Number(invItem.parLevel || 0);
    const missingQty = Math.max(0, par - currentQty);

    return {
      ...invItem,
      currentQty,
      countedQty: currentQty,
      replenishedQty: Number(invItem.replenishedQty || 0) + qtyToIssue,
      missingQty,
      status: missingQty > 0 ? "below_par" : "at_par",
      checkStatus: missingQty > 0 ? "below_par" : "par",
    };
  }

  // Component / nested kit item
  if (invItem.type === "nestedKit" && Array.isArray(invItem.nestedItems)) {
    const updatedNestedItems = invItem.nestedItems.map((nested) => {
      const nestedId = nested.kitItemId || nested.id;

      if (nestedId !== issuedId) return nested;

      const currentQty = Number(nested.currentQty || 0) + qtyToIssue;
      const par = Number(nested.parLevel || 0);
      const missingQty = Math.max(0, par - currentQty);

      return {
        ...nested,
        currentQty,
        countedQty: currentQty,
        replenishedQty: Number(nested.replenishedQty || 0) + qtyToIssue,
        missingQty,
        status: missingQty > 0 ? "below_par" : "at_par",
        checkStatus: missingQty > 0 ? "below_par" : "par",
      };
    });

    const stillMissingNested = updatedNestedItems.some(
      (nested) => Number(nested.missingQty || 0) > 0
    );

    return {
      ...invItem,
      nestedItems: updatedNestedItems,
      missingQty: stillMissingNested ? 1 : 0,
      status: stillMissingNested ? "below_par" : "at_par",
      checkStatus: stillMissingNested ? "below_par" : "par",
    };
  }

  return invItem;
});

        await addDoc(collection(db, "inventoryTransactions"), {
          type: "issue",
          itemName: item.itemName,
          quantity: qtyToIssue,
          fromLocationId: sourceLocationId,
          toLocationId: inventory.locationId,
          dailyInventoryId: inventory.id || "",
          createdAt: serverTimestamp(),
          createdBy: "Logistics Officer",
        });
      }

      const actionRequired =
  getActionItemsFromInventory(updatedInventoryItems).length > 0 ||
  remainingRequestItems.length > 0;

if (inventory.id) {
  await updateDoc(doc(db, "dailyInventories", inventory.id), {
    items: updatedInventoryItems,
    actionRequired,
    status: actionRequired ? "in_progress" : "submitted",
    updatedAt: serverTimestamp(),
  });
}

if (inventory.supplyRequestIds?.length) {
  const stillShort = skippedItems.length > 0;

  for (const requestId of inventory.supplyRequestIds) {
    await updateDoc(doc(db, "supplyRequests", requestId), {
  status: stillShort ? "pending" : "issued",
  items: stillShort ? remainingRequestItems : [],
  issuedAt: stillShort ? null : serverTimestamp(),
  updatedAt: serverTimestamp(),
});
  }
}

      if (skippedItems.length > 0) {
  alert(
    "Some items could not be issued because there was not enough stock:\n\n" +
      skippedItems.join("\n")
  );
}

      await onIssueComplete();
      onClose();
    } catch (err) {
      console.error("Failed to issue items:", err);
      alert("Failed to issue items. Check console for details.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>Issue Items</h2>
            <p style={styles.subtitle}>Select items to issue</p>
          </div>

          <button onClick={onClose} style={styles.closeButton}>
            Close
          </button>
        </div>

        <label style={styles.fieldLabel}>Issue From</label>
        <select
          value={sourceLocationId}
          onChange={(e) => setSourceLocationId(e.target.value)}
          style={styles.select}
        >
          {SOURCE_LOCATIONS.map((loc) => (
            <option key={loc.id} value={loc.id}>
              {loc.label}
            </option>
          ))}
        </select>

        <div style={{ marginTop: 20 }}>
          {selectedItems.map((item) => (
            <div key={item.itemName} style={styles.issueRow}>
              <input
                type="checkbox"
                checked={item.checked}
                onChange={() => toggleItem(item.itemName)}
              />

              <div style={{ flex: 1 }}>
                <strong>{item.itemName}</strong>

                <div style={{ fontSize: 13, color: "#64748b" }}>
                  Current: {item.currentQty || 0} / PAR {item.parLevel || 0} ·
                  Missing: {item.missingQty || 0}
                </div>
              </div>

              <input
                type="number"
                min="0"
                value={item.issueQty}
                onChange={(e) => updateQty(item.itemName, e.target.value)}
                style={styles.qtyInput}
              />
            </div>
          ))}
        </div>

        <div style={styles.modalActions}>
          <button onClick={onClose} style={styles.secondaryButton}>
            Cancel
          </button>

          <button
            onClick={handleIssue}
            disabled={saving}
            style={{
              ...styles.primaryButton,
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? "Issuing..." : "Issue Selected Items"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReceiveStockModal({ stockItems, onClose, onReceiveComplete }) {
  const [selectedStockId, setSelectedStockId] = useState(stockItems[0]?.id || "");
  const [fromLocationId, setFromLocationId] = useState("shipment");
  const [toLocationId, setToLocationId] = useState("onsiteStock");
  const [quantity, setQuantity] = useState(1);
  const [expirationDate, setExpirationDate] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const selectedStock = stockItems.find((item) => item.id === selectedStockId);

  const sortedStockItems = useMemo(() => {
  return [...stockItems].sort((a, b) =>
    String(a.itemName || "").localeCompare(String(b.itemName || ""))
  );
}, [stockItems]);

  const inventoryLocations = [
    { id: "shipment", label: "Shipment Received" },
    { id: "centralSupply", label: "Central Supply" },
    { id: "onsiteStock", label: "On Site Supply" },
    { id: "hq", label: "HQ" },
    { id: "cart3", label: "Cart 3" },
    { id: "cart5", label: "Cart 5" },
    { id: "cart9", label: "Cart 9" },
    { id: "cart12", label: "Cart 12" },
    { id: "cart15", label: "Cart 15" },
    { id: "cart17", label: "Cart 17" },
    { id: "cartMD", label: "MD Cart" },
    { id: "bike1", label: "Bike 1" },
    { id: "bike2", label: "Bike 2" },
    { id: "bike3", label: "Bike 3" },
    { id: "tent3", label: "Tent 3" },
    { id: "tent5", label: "Tent 5" },
    { id: "tent9", label: "Tent 9" },
    { id: "tent12", label: "Tent 12" },
    { id: "tent17", label: "Tent 17" },
  ];

  async function findStockItem(locationId, itemName) {
    const stockQuery = query(
      collection(db, "inventoryStock"),
      where("locationId", "==", locationId),
      where("itemName", "==", itemName)
    );

    const snap = await getDocs(stockQuery);
    return snap.empty ? null : snap.docs[0];
  }

  async function handleReceive() {
  if (saving) return;
  if (!selectedStock) return;

  const moveQty = Number(quantity || 0);

  if (moveQty <= 0) {
    alert("Enter a quantity greater than 0.");
    return;
  }

  if (fromLocationId === toLocationId) {
    alert("From and To locations cannot be the same.");
    return;
  }

  try {
    setSaving(true);

    const itemName = selectedStock.itemName;

    let incomingLots = [
      {
        expirationDate: expirationDate || selectedStock.expirationDate || "",
        quantity: moveQty,
      },
    ];

    if (fromLocationId !== "shipment") {
      const sourceDoc = await findStockItem(fromLocationId, itemName);

      if (!sourceDoc) {
        alert("No source stock record found for this item.");
        setSaving(false);
        return;
      }

      const sourceData = sourceDoc.data();
      const sourceLots = normalizeLots(sourceData);
      const sourceTotal = lotTotal(sourceLots);

      if (sourceTotal < moveQty) {
        alert(`Not enough stock at source. Available: ${sourceTotal}`);
        setSaving(false);
        return;
      }

      const result = subtractLots(sourceLots, moveQty);
      incomingLots = result.issuedLots;

      await updateDoc(doc(db, "inventoryStock", sourceDoc.id), {
        lots: result.updatedLots,
        quantityOnHand: lotTotal(result.updatedLots),
        updatedAt: serverTimestamp(),
      });
    }

    const destinationDoc = await findStockItem(toLocationId, itemName);

    if (destinationDoc) {
      const destinationData = destinationDoc.data();
      const destinationLots = normalizeLots(destinationData);
      const newLots = addLots(destinationLots, incomingLots);

      await updateDoc(doc(db, "inventoryStock", destinationDoc.id), {
        lots: newLots,
        quantityOnHand: lotTotal(newLots),
        updatedAt: serverTimestamp(),
      });
    } else {
      await addDoc(collection(db, "inventoryStock"), {
        locationId: toLocationId,
        itemName,
        category: selectedStock.category || "Uncategorized",
        shelfBin: "",
        expirationDate: expirationDate || selectedStock.expirationDate || null,
        supplier: selectedStock.supplier || "",
        itemNumber: selectedStock.itemNumber || "",
        peoplesoftNumber: selectedStock.peoplesoftNumber || "",
        psMfgNumber: selectedStock.psMfgNumber || "",
        manufacturerNumber: selectedStock.manufacturerNumber || "",
        vendorPartNumber: selectedStock.vendorPartNumber || "",
        unitType: selectedStock.unitType || "each",
        orderPackType: selectedStock.orderPackType || "each",
        unitsPerPack: Number(selectedStock.unitsPerPack || 1),
        price: selectedStock.price ?? null,
        lots: incomingLots,
        quantityOnHand: lotTotal(incomingLots),
        parLevel: selectedStock.parLevel || 0,
        reorderLevel: selectedStock.reorderLevel || 0,
        active: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }

    await addDoc(collection(db, "inventoryTransactions"), {
      type: fromLocationId === "shipment" ? "receive" : "transfer",
      itemName,
      quantity: moveQty,
      fromLocationId,
      toLocationId,
      note: note.trim(),
      createdAt: serverTimestamp(),
      createdBy: "Logistics Officer",
    });

    await onReceiveComplete();
    onClose();
  } catch (err) {
    console.error("Failed to move/receive stock:", err);
    alert("Failed to move/receive stock. Check console.");
  } finally {
    setSaving(false);
  }
}

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>Move / Receive Supplies</h2>
            <p style={styles.subtitle}>
              Receive shipments or move supplies between locations.
            </p>
          </div>

          <button onClick={onClose} style={styles.closeButton}>
            Close
          </button>
        </div>

        <div style={{ marginTop: 20 }}>
          <label style={styles.fieldLabel}>Item</label>
          <select
            value={selectedStockId}
            onChange={(e) => setSelectedStockId(e.target.value)}
            style={styles.select}
          >
            {sortedStockItems.map((item) => (
              <option key={item.id} value={item.id}>
                {item.itemName} — {item.locationId} — Current:{" "}
                {item.quantityOnHand || 0}
              </option>
            ))}
          </select>

          <label style={styles.fieldLabel}>From</label>
          <select
            value={fromLocationId}
            onChange={(e) => setFromLocationId(e.target.value)}
            style={styles.select}
          >
            {inventoryLocations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.label}
              </option>
            ))}
          </select>

          <label style={styles.fieldLabel}>To</label>
          <select
            value={toLocationId}
            onChange={(e) => setToLocationId(e.target.value)}
            style={styles.select}
          >
            {inventoryLocations
              .filter((loc) => loc.id !== "shipment")
              .map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.label}
                </option>
              ))}
          </select>

          <label style={styles.fieldLabel}>Quantity</label>
          <input
            type="number"
            min="1"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            style={styles.input}
          />

          <label style={styles.fieldLabel}>Expiration Date Optional</label>
          <input
            type="date"
            value={expirationDate}
            onChange={(e) => setExpirationDate(e.target.value)}
            style={styles.input}
          />

          <label style={styles.fieldLabel}>Note</label>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note"
            style={styles.input}
          />
        </div>

        <div style={styles.modalActions}>
          <button onClick={onClose} style={styles.secondaryButton}>
            Cancel
          </button>

          <button
            onClick={handleReceive}
            disabled={saving}
            style={{
              ...styles.primaryButton,
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? "Saving..." : "Complete Move / Receive"}
          </button>
        </div>
      </div>
    </div>
  );
}

async function syncInventoryItemReferences(itemId, updates) {
  const kitSnap = await getDocs(collection(db, "inventoryKitTemplates"));

  for (const kitDoc of kitSnap.docs) {
    const kit = kitDoc.data();
    let changed = false;

    const updatedLocations = (kit.locations || []).map((loc) => ({
      ...loc,
      items: (loc.items || []).map((kitItem) => {
        if (kitItem.inventoryStockId !== itemId) return kitItem;

        changed = true;

        return {
          ...kitItem,
          itemName: updates.itemName,
          category: updates.category,
          unitType: updates.unitType,
          supplier: updates.supplier,
          itemNumber: updates.itemNumber,
          vendorPartNumber: updates.vendorPartNumber,
        };
      }),
    }));

    if (changed) {
      await updateDoc(doc(db, "inventoryKitTemplates", kitDoc.id), {
        locations: updatedLocations,
        updatedAt: serverTimestamp(),
      });
    }
  }
}

function AddInventoryItemModal({
  categories,
  onClose,
  onAddComplete,
}) {
  const [itemName, setItemName] = useState("");
  const [locationId, setLocationId] = useState("onsiteStock");
  const [quantityOnHand, setQuantityOnHand] = useState(0);
  const [parLevel, setParLevel] = useState(0);
  const [reorderLevel, setReorderLevel] = useState(0);
  const [category, setCategory] = useState("");
  const [shelfBin, setShelfBin] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [supplier, setSupplier] = useState("");
  const [itemNumber, setItemNumber] = useState("");
  const [peoplesoftNumber, setPeoplesoftNumber] = useState("");
  const [psMfgNumber, setPsMfgNumber] = useState("");
  const [manufacturerNumber, setManufacturerNumber] = useState("");
  const [vendorPartNumber, setVendorPartNumber] = useState("");
  const [unitType, setUnitType] = useState("vial");
  const [orderPackType, setOrderPackType] = useState("box");
  const [unitsPerPack, setUnitsPerPack] = useState(25);
  const [price, setPrice] = useState("");
  const [saving, setSaving] = useState(false);

  async function handleAdd() {
    if (saving) return;

    if (!itemName.trim()) {
      alert("Enter an item name.");
      return;
    }

    try {
  setSaving(true);

  await addDoc(collection(db, "inventoryStock"), {
    locationId,
    itemName: itemName.trim(),
    category: category.trim() || "Uncategorized",
    shelfBin: shelfBin.trim(),
    expirationDate: expirationDate || null,
    supplier: supplier.trim(),
    itemNumber: itemNumber.trim(),
    peoplesoftNumber: peoplesoftNumber.trim(),
    psMfgNumber: psMfgNumber.trim(),
    manufacturerNumber: manufacturerNumber.trim(),
    vendorPartNumber: vendorPartNumber.trim(),
    unitType,
    orderPackType,
    unitsPerPack: Number(unitsPerPack || 1),
    price: price === "" ? null : Number(price),
    quantityOnHand: Number(quantityOnHand || 0),
    parLevel: Number(parLevel || 0),
    reorderLevel: Number(reorderLevel || 0),
    active: true,
    updatedAt: serverTimestamp(),
    createdAt: serverTimestamp(),
  });

      await addDoc(collection(db, "inventoryTransactions"), {
        type: "new_item",
        itemName: itemName.trim(),
        locationId,
        quantity: Number(quantityOnHand || 0),
        createdAt: serverTimestamp(),
        createdBy: "Logistics Officer",
      });

      await onAddComplete();
      onClose();
    } catch (err) {
      console.error("Failed to add inventory item:", err);
      alert("Failed to add inventory item.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>
              Add Inventory Item
            </h2>

            <p style={styles.subtitle}>
              Create a new inventory stock item.
            </p>
          </div>

          <button onClick={onClose} style={styles.closeButton}>
            Close
          </button>
        </div>

        <div style={{ marginTop: 20 }}>
          <label style={styles.fieldLabel}>Item Name</label>

          <input
            value={itemName}
            onChange={(e) => setItemName(e.target.value)}
            style={styles.input}
          />

          <label style={styles.fieldLabel}>Category</label>

<select
  value={category}
  onChange={(e) => setCategory(e.target.value)}
  style={styles.select}
>
  <option value="">Select Category</option>

  {categories.map((cat) => (
    <option key={cat.id} value={cat.name}>
      {cat.name}
    </option>
  ))}
</select>

<label style={styles.fieldLabel}>Shelf / Bin Location</label>

<input
  value={shelfBin}
  onChange={(e) => setShelfBin(e.target.value)}
  placeholder="Shelf A / Bin 2"
  style={styles.input}
/>

<label style={styles.fieldLabel}>Expiration Date Optional</label>

<input
  type="date"
  value={expirationDate}
  onChange={(e) => setExpirationDate(e.target.value)}
  style={styles.input}
/>

<label style={styles.fieldLabel}>Supplier</label>

<input
  value={supplier}
  onChange={(e) => setSupplier(e.target.value)}
  placeholder="Bound Tree, McKesson, Amazon, etc."
  style={styles.input}
/>

<label style={styles.fieldLabel}>Item / Part Number</label>

<input
  value={itemNumber}
  onChange={(e) => setItemNumber(e.target.value)}
  style={styles.input}
/>

<label style={styles.fieldLabel}>Peoplesoft #</label>
<input
  value={peoplesoftNumber}
  onChange={(e) => setPeoplesoftNumber(e.target.value)}
  style={styles.input}
/>

<label style={styles.fieldLabel}>PS Mfg No.</label>
<input
  value={psMfgNumber}
  onChange={(e) => setPsMfgNumber(e.target.value)}
  style={styles.input}
/>

<label style={styles.fieldLabel}>Mfg #</label>
<input
  value={manufacturerNumber}
  onChange={(e) => setManufacturerNumber(e.target.value)}
  style={styles.input}
/>

<label style={styles.fieldLabel}>Vendor Part #</label>
<input
  value={vendorPartNumber}
  onChange={(e) => setVendorPartNumber(e.target.value)}
  style={styles.input}
/>

<label style={styles.fieldLabel}>Order Pack Type</label>

<select
  value={orderPackType}
  onChange={(e) => setOrderPackType(e.target.value)}
  style={styles.select}
>
  <option value="each">Each</option>
  <option value="vial">Vial</option>
  <option value="ampule">Ampule</option>
  <option value="box">Box</option>
  <option value="case">Case</option>
  <option value="pack">Pack</option>
  <option value="roll">Roll</option>
  <option value="bag">Bag</option>
  <option value="set">Set</option>
  <option value="unit">Unit</option>
</select>

<label style={styles.fieldLabel}>Units Per Pack</label>

<input
  type="number"
  min="1"
  value={unitsPerPack}
  onChange={(e) => setUnitsPerPack(e.target.value)}
  style={styles.input}
/>

<label style={styles.fieldLabel}>Price</label>

<input
  type="number"
  min="0"
  step="0.01"
  value={price}
  onChange={(e) => setPrice(e.target.value)}
  style={styles.input}
/>

          <label style={styles.fieldLabel}>Location</label>

          <select
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            style={styles.select}
          >
            <option value="onsiteStock">
              On Site Stock
            </option>

            <option value="centralSupply">
              Central Supply
            </option>
          </select>

          <label style={styles.fieldLabel}>
            Starting Quantity
          </label>

          <input
            type="number"
            value={quantityOnHand}
            onChange={(e) =>
              setQuantityOnHand(e.target.value)
            }
            style={styles.input}
          />

          <label style={styles.fieldLabel}>
            Par Level
          </label>

          <input
            type="number"
            value={parLevel}
            onChange={(e) =>
              setParLevel(e.target.value)
            }
            style={styles.input}
          />

          <label style={styles.fieldLabel}>
            Reorder Level
          </label>

          <input
            type="number"
            value={reorderLevel}
            onChange={(e) =>
              setReorderLevel(e.target.value)
            }
            style={styles.input}
          />
        </div>

        <div style={styles.modalActions}>
          <button
            onClick={onClose}
            style={styles.secondaryButton}
          >
            Cancel
          </button>

          <button
            onClick={handleAdd}
            disabled={saving}
            style={{
              ...styles.primaryButton,
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? "Saving..." : "Add Item"}
          </button>
        </div>
      </div>
    </div>
  );
}
function EditInventoryItemModal({
  item,
  categories,
  onClose,
  onSaveComplete,
}) {
 const [form, setForm] = useState({
  itemName: item.itemName || "",
  category: item.category || "",
  supplier: item.supplier || "",
  itemNumber: item.itemNumber || "",
  peoplesoftNumber: item.peoplesoftNumber || "",
  psMfgNumber: item.psMfgNumber || "",
  manufacturerNumber: item.manufacturerNumber || "",
  vendorPartNumber: item.vendorPartNumber || "",
  notes: item.notes || "",

  unitType: item.unitType || "each",
  orderPackType: item.orderPackType || "box",
  unitsPerPack: item.unitsPerPack || 1,
  price: item.price || "",

  parLevel: item.parLevel || 0,
  reorderLevel: item.reorderLevel || 0,
});

  const [lots, setLots] = useState(
  Array.isArray(item.allLots) && item.allLots.length > 0
    ? item.allLots.map((lot) => ({
        ...lot,
        locationId: lot.locationId || "centralSupply",
        shelfBin: lot.shelfBin || "",
      }))
    : []
);

  const [saving, setSaving] = useState(false);

 function updateLot(index, field, value) {
  setLots((prev) =>
    prev.map((lot, i) =>
      i === index
        ? {
            ...lot,
            [field]:
              field === "quantity"
                ? Number(value || 0)
                : value,
          }
        : lot
    )
  );
}

  function addLot() {
  setLots((prev) => [
    ...prev,
    {
      locationId: "centralSupply",
      expirationDate: "",
      quantity: 0,
      shelfBin: "",
    },
  ]);
}

  function removeLot(index) {
    setLots((prev) => prev.filter((_, i) => i !== index));
  }

  const totalQuantity = lots.reduce(
  (sum, lot) => sum + Number(lot.quantity || 0),
  0
);

const centralQty = lots
  .filter((lot) => lot.locationId === "centralSupply")
  .reduce((sum, lot) => sum + Number(lot.quantity || 0), 0);

const onsiteQty = lots
  .filter((lot) => lot.locationId === "onsiteStock")
  .reduce((sum, lot) => sum + Number(lot.quantity || 0), 0);

  async function handleSave() {
  try {
    setSaving(true);

    const cleanedLots = lots
      .map((lot) => ({
        locationId: lot.locationId || "centralSupply",
        expirationDate: lot.expirationDate || "",
        quantity: Number(lot.quantity || 0),
        shelfBin: lot.shelfBin || "",
      }))
      .filter((lot) => Number(lot.quantity || 0) > 0);

    const sourceRecords = item.sourceRecords || [{ id: item.id }];

    for (const record of sourceRecords) {
      const locationId = record.locationId || "centralSupply";

      const lotsForThisRecord = cleanedLots
        .filter((lot) => lot.locationId === locationId)
        .map((lot) => ({
          expirationDate: lot.expirationDate || "",
          quantity: Number(lot.quantity || 0),
          shelfBin: lot.shelfBin || "",
        }));

      await updateDoc(doc(db, "inventoryStock", record.id), {
        ...form,
        locationId,
        lots: lotsForThisRecord,
        quantityOnHand: lotTotal(lotsForThisRecord),
        shelfBin:
          locationId === "centralSupply"
            ? form.centralSupplyBin || ""
            : locationId === "onsiteStock"
            ? form.onsiteStockBin || ""
            : record.shelfBin || "",
        active: true,
        updatedAt: serverTimestamp(),
      });
    }

    const existingLocationIds = new Set(
      sourceRecords.map((record) => record.locationId)
    );

    const newLocationIds = [
      ...new Set(cleanedLots.map((lot) => lot.locationId)),
    ].filter((locationId) => !existingLocationIds.has(locationId));

    for (const locationId of newLocationIds) {
      const lotsForNewLocation = cleanedLots
        .filter((lot) => lot.locationId === locationId)
        .map((lot) => ({
          expirationDate: lot.expirationDate || "",
          quantity: Number(lot.quantity || 0),
          shelfBin: lot.shelfBin || "",
        }));

      await addDoc(collection(db, "inventoryStock"), {
        ...form,
        locationId,
        lots: lotsForNewLocation,
        quantityOnHand: lotTotal(lotsForNewLocation),
        shelfBin:
          locationId === "centralSupply"
            ? form.centralSupplyBin || ""
            : locationId === "onsiteStock"
            ? form.onsiteStockBin || ""
            : "",
        active: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }

    await syncInventoryItemReferences(item.id, form);

    await onSaveComplete?.();
    onClose();
  } catch (err) {
    console.error(err);
    alert("Failed to save inventory item.");
  } finally {
    setSaving(false);
  }
}

  return (
    <div style={styles.modalOverlay}>
     <div
  style={{
    ...styles.modal,
    maxWidth: 900,
    maxHeight: "90vh",
    overflowY: "auto",
  }}
>
        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>View / Edit Inventory Item</h2>
            <p style={styles.subtitle}>
              Manage stock details and expiration lots.
            </p>
          </div>

          <button style={styles.secondaryButton} onClick={onClose}>
            Close
          </button>
        </div>

<div
  style={{
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
    marginTop: 20,
  }}
>
  {[
    ["Item Name", "itemName"],
    ["Supplier", "supplier"],
    ["Central Supply Shelf / Bin", "centralSupplyBin"],
    ["On-Site Supply Shelf / Bin", "onsiteStockBin"],
    ["Item Number", "itemNumber"],
    ["Vendor Part Number", "vendorPartNumber"],
    ["PeopleSoft Number", "peoplesoftNumber"],
  ].map(([label, field]) => (
    <div key={field}>
      <label style={styles.fieldLabel}>{label}</label>

      <input
        value={form[field] || ""}
        onChange={(e) =>
          setForm((prev) => ({
            ...prev,
            [field]: e.target.value,
          }))
        }
        style={styles.input}
      />
    </div>
  ))}

  <div>
    <label style={styles.fieldLabel}>Category</label>

    <select
  value={form.category || ""}
  onChange={(e) =>
    setForm((prev) => ({
      ...prev,
      category: e.target.value,
    }))
  }
  style={styles.input}
>
  <option value="">Select Category</option>

  {categories.map((cat) => (
    <option key={cat.id} value={cat.name}>
      {cat.name}
    </option>
  ))}
</select>
  </div>

  <div>
    <label style={styles.fieldLabel}>Unit Type</label>

    <select
      value={form.unitType || "each"}
      onChange={(e) =>
        setForm((prev) => ({
          ...prev,
          unitType: e.target.value,
        }))
      }
      style={styles.input}
    >
      <option value="each">each</option>
      <option value="box">box</option>
      <option value="case">case</option>
      <option value="bag">bag</option>
      <option value="vial">vial</option>
      <option value="roll">roll</option>
      <option value="pack">pack</option>
    </select>
  </div>

  <div>
    <label style={styles.fieldLabel}>PAR Level</label>

    <input
      type="number"
      value={form.parLevel || 0}
      onChange={(e) =>
        setForm((prev) => ({
          ...prev,
          parLevel: Number(e.target.value),
        }))
      }
      style={styles.input}
    />
  </div>

  <div>
    <label style={styles.fieldLabel}>Reorder Level</label>

    <input
      type="number"
      value={form.reorderLevel || 0}
      onChange={(e) =>
        setForm((prev) => ({
          ...prev,
          reorderLevel: Number(e.target.value),
        }))
      }
      style={styles.input}
    />
  </div>
</div>

        <div
  style={{
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 12,
    marginTop: 24,
  }}
>
  <div style={styles.detailBox}>
    <div style={styles.detailLabel}>Total Quantity</div>
    <div style={styles.detailValue}>{totalQuantity}</div>
  </div>

  <div style={styles.detailBox}>
    <div style={styles.detailLabel}>Central Supply</div>
    <div style={styles.detailValue}>{centralQty}</div>
  </div>

  <div style={styles.detailBox}>
    <div style={styles.detailLabel}>On Site Supply</div>
    <div style={styles.detailValue}>{onsiteQty}</div>
  </div>
</div>

        <div style={{ marginTop: 24 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <div>
              <h3 style={{ margin: 0 }}>Expiration Lots</h3>
              <div
                style={{
                  fontSize: 13,
                  color: "#64748b",
                }}
              >
                Total Quantity: {totalQuantity}
              </div>
            </div>

            <button
              style={styles.secondaryButton}
              onClick={addLot}
            >
              + Add Lot
            </button>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            {lots.map((lot, index) => {
              const isExpired =
                lot.expirationDate &&
                new Date(lot.expirationDate) < new Date();

              return (
                <div
                  key={index}
                  style={{
                    border: `1px solid ${
                      isExpired ? "#ef4444" : "#dbe4ee"
                    }`,
                    borderRadius: 12,
                    padding: 12,
                    background: isExpired
                      ? "#fef2f2"
                      : "#fff",
                    display: "grid",
                    gridTemplateColumns:
                    "180px 1fr 140px 180px auto",
                    gap: 10,
                    alignItems: "center",
                  }}
                >
                   <div>
  <label
    style={{
      fontSize: 12,
      fontWeight: 700,
      color: "#475569",
    }}
  >
    Location
  </label>

  <select
    value={lot.locationId || "centralSupply"}
    onChange={(e) =>
      updateLot(index, "locationId", e.target.value)
    }
    style={{
      ...styles.input,
      marginTop: 6,
    }}
  >
    <option value="centralSupply">
      Central Supply
    </option>

    <option value="onsiteStock">
      On Site Supply
    </option>
  </select>
</div>

<div>
  <label
    style={{
      fontSize: 12,
      fontWeight: 700,
      color: "#475569",
    }}
  >
    Expiration Date
  </label>

  <input
    type="date"
    value={lot.expirationDate || ""}
    onChange={(e) =>
      updateLot(index, "expirationDate", e.target.value)
    }
    style={{
      ...styles.input,
      marginTop: 6,
    }}
  />
</div>

<div>
  <label
    style={{
      fontSize: 12,
      fontWeight: 700,
      color: "#475569",
    }}
  >
    Quantity
  </label>

  <input
    type="number"
    value={lot.quantity || 0}
    onChange={(e) =>
      updateLot(index, "quantity", e.target.value)
    }
    style={{
      ...styles.input,
      marginTop: 6,
    }}
  />
</div>

<div>
  <label
    style={{
      fontSize: 12,
      fontWeight: 700,
      color: "#475569",
    }}
  >
    Shelf / Bin
  </label>

  <input
    value={lot.shelfBin || ""}
    onChange={(e) =>
      updateLot(index, "shelfBin", e.target.value)
    }
    style={{
      ...styles.input,
      marginTop: 6,
    }}
  />
</div>

<button
  style={styles.smallDangerButton}
  onClick={() => removeLot(index)}
>
  Remove
</button> 
                </div>
              );
            })}
          </div>
        </div>

        <div
          style={{
            marginTop: 24,
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
          }}
        >
          <button
            style={styles.secondaryButton}
            onClick={onClose}
          >
            Cancel
          </button>

          <button
            style={styles.primaryButton}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function QuickAdjustQuantityModal({ item, onClose, onSaved }) {
  const locationOptions = [
    { id: "centralSupply", label: "Central Supply" },
    { id: "onsiteStock", label: "On Site Supply" },
    { id: "cart3", label: "Cart 3" },
    { id: "cart5", label: "Cart 5" },
    { id: "cart9", label: "Cart 9" },
    { id: "cart12", label: "Cart 12" },
    { id: "cart15", label: "Cart 15" },
    { id: "cart17", label: "Cart 17" },
    { id: "mdcart", label: "MD Cart" },
    { id: "bike1", label: "Bike 1" },
    { id: "bike2", label: "Bike 2" },
    { id: "bike3", label: "Bike 3" },
    { id: "tent3", label: "Tent 3" },
    { id: "tent5", label: "Tent 5" },
    { id: "tent9", label: "Tent 9" },
    { id: "tent12", label: "Tent 12" },
    { id: "tent17", label: "Tent 17" },
    { id: "medicalhq", label: "Medical HQ" },
  ];

  const existingRecords = item.sourceRecords || [];

  const [rows, setRows] = useState(() =>
    existingRecords.map((record) => ({
      id: record.id,
      locationId: record.locationId || "",
      quantity: Number(record.quantityOnHand || 0),
      lots: normalizeLots(record),
      existing: true,
    }))
  );

  const [newLocationId, setNewLocationId] = useState("centralSupply");
  const [newQuantity, setNewQuantity] = useState(0);
  const [saving, setSaving] = useState(false);

  function updateRow(index, quantity) {
    setRows((prev) =>
      prev.map((row, i) =>
        i === index
          ? {
              ...row,
              quantity: Number(quantity || 0),
            }
          : row
      )
    );
  }

  function updateLot(rowIndex, lotIndex, field, value) {
  setRows((prev) =>
    prev.map((row, rIndex) =>
      rIndex === rowIndex
        ? {
            ...row,
            lots: row.lots.map((lot, lIndex) =>
              lIndex === lotIndex
                ? {
                    ...lot,
                    [field]:
                      field === "quantity"
                        ? Number(value || 0)
                        : value,
                  }
                : lot
            ),
          }
        : row
    )
  );
}

function addLotToRow(rowIndex) {
  setRows((prev) =>
    prev.map((row, rIndex) =>
      rIndex === rowIndex
        ? {
            ...row,
            lots: [
              ...(row.lots || []),
              { expirationDate: "", quantity: 0 },
            ],
          }
        : row
    )
  );
}

function removeLotFromRow(rowIndex, lotIndex) {
  setRows((prev) =>
    prev.map((row, rIndex) =>
      rIndex === rowIndex
        ? {
            ...row,
            lots: row.lots.filter((_, lIndex) => lIndex !== lotIndex),
          }
        : row
    )
  );
}

 async function handleSave() {
  if (saving) return;

  try {
    setSaving(true);

    for (const row of rows) {
      const cleanedLots = (row.lots || [])
        .map((lot) => ({
          expirationDate: lot.expirationDate || "",
          quantity: Number(lot.quantity || 0),
        }))
        .filter((lot) => Number(lot.quantity || 0) > 0);

      await updateDoc(doc(db, "inventoryStock", row.id), {
        lots: cleanedLots,
        quantityOnHand: lotTotal(cleanedLots),
        updatedAt: serverTimestamp(),
      });

      await addDoc(collection(db, "inventoryTransactions"), {
        type: "manual_lot_adjustment",
        itemName: item.itemName || "",
        locationId: row.locationId,
        quantity: lotTotal(cleanedLots),
        note: "Manual lot / quantity adjustment",
        createdAt: serverTimestamp(),
        createdBy: "Logistics Officer",
      });
    }

    if (Number(newQuantity || 0) > 0) {
      await addDoc(collection(db, "inventoryStock"), {
        locationId: newLocationId,
        itemName: item.itemName || "",
        category: item.category || "Uncategorized",
        supplier: item.supplier || "",
        itemNumber: item.itemNumber || "",
        vendorPartNumber: item.vendorPartNumber || "",
        unitType: item.unitType || "each",
        parLevel: item.parLevel || 0,
        reorderLevel: item.reorderLevel || 0,
        lots: [{ expirationDate: "", quantity: Number(newQuantity || 0) }],
        quantityOnHand: Number(newQuantity || 0),
        active: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }

    await onSaved?.();
    onClose();
  } catch (err) {
    console.error("Failed to adjust lots:", err);
    alert("Failed to adjust lots.");
  } finally {
    setSaving(false);
  }
}

  return (
    <div style={styles.modalOverlay}>
      <div style={{ ...styles.modal, maxWidth: 820 }}>
        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>Adjust Quantity</h2>
            <p style={styles.subtitle}>{item.itemName}</p>
          </div>

          <button onClick={onClose} style={styles.closeButton}>
            Close
          </button>
        </div>

        <div style={{ marginTop: 20 }}>
          {rows.map((row, rowIndex) => (
  <div
    key={row.id}
    style={{
      border: "1px solid #e2e8f0",
      borderRadius: 14,
      padding: 14,
      marginBottom: 14,
    }}
  >
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <strong>
        {locationOptions.find((loc) => loc.id === row.locationId)?.label ||
          row.locationId}
      </strong>

      <button
        style={styles.smallButton}
        onClick={() => addLotToRow(rowIndex)}
      >
        + Add Lot
      </button>
    </div>

    {(row.lots || []).map((lot, lotIndex) => (
      <div
        key={lotIndex}
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 120px auto",
          gap: 10,
          marginTop: 10,
          alignItems: "center",
        }}
      >
        <input
          type="date"
          value={lot.expirationDate || ""}
          onChange={(e) =>
            updateLot(rowIndex, lotIndex, "expirationDate", e.target.value)
          }
          style={styles.input}
        />

        <input
          type="number"
          min="0"
          value={lot.quantity || 0}
          onChange={(e) =>
            updateLot(rowIndex, lotIndex, "quantity", e.target.value)
          }
          style={styles.input}
        />

        <button
          style={styles.smallDangerButton}
          onClick={() => removeLotFromRow(rowIndex, lotIndex)}
        >
          Remove
        </button>
      </div>
    ))}

    <div style={{ marginTop: 8, fontWeight: 800 }}>
      Location Total: {lotTotal(row.lots || [])}
    </div>
  </div>
))}

          <div style={{ marginTop: 24 }}>
            <h3>Add Quantity at Another Location</h3>

            <label style={styles.fieldLabel}>Location</label>
            <select
              value={newLocationId}
              onChange={(e) => setNewLocationId(e.target.value)}
              style={styles.select}
            >
              {locationOptions.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.label}
                </option>
              ))}
            </select>

            <label style={styles.fieldLabel}>Quantity</label>
            <input
              type="number"
              min="0"
              value={newQuantity}
              onChange={(e) => setNewQuantity(e.target.value)}
              style={styles.input}
            />
          </div>
        </div>

        <div style={styles.modalActions}>
          <button onClick={onClose} style={styles.secondaryButton}>
            Cancel
          </button>

          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              ...styles.primaryButton,
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? "Saving..." : "Save Quantity Adjustments"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ManageCategoriesModal({ categories, stockItems, onClose, onSaved }) {
  const [newCategory, setNewCategory] = useState("");
  const [saving, setSaving] = useState(false);

  function slugifyCategory(name) {
    return String(name || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "")
      .slice(0, 40);
  }

  function itemCountForCategory(categoryName) {
    return stockItems.filter(
      (item) => String(item.category || "") === String(categoryName || "")
    ).length;
  }

  async function handleAddCategory() {
    const name = newCategory.trim();

    if (!name) {
      alert("Enter a category name.");
      return;
    }

    const exists = categories.some(
      (cat) => String(cat.name || "").toLowerCase() === name.toLowerCase()
    );

    if (exists) {
      alert("That category already exists.");
      return;
    }

    try {
      setSaving(true);

      await setDoc(doc(db, "inventoryCategories", slugifyCategory(name)), {
        name,
        active: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setNewCategory("");
      await onSaved();
    } catch (err) {
      console.error("Failed to add category:", err);
      alert("Failed to add category.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDisableCategory(category) {
    const count = itemCountForCategory(category.name);

    const confirmed = window.confirm(
      count > 0
        ? `${category.name} is assigned to ${count} item(s). Disable it anyway? Existing items will keep this category, but it will disappear from new item dropdowns.`
        : `Disable ${category.name}?`
    );

    if (!confirmed) return;

    try {
      setSaving(true);

      await updateDoc(doc(db, "inventoryCategories", category.id), {
        active: false,
        updatedAt: serverTimestamp(),
      });

      await onSaved();
    } catch (err) {
      console.error("Failed to disable category:", err);
      alert("Failed to disable category.");
    } finally {
      setSaving(false);
    }
  }

  async function handleRenameCategory(category) {
    const nextName = window.prompt("New category name:", category.name);
    if (!nextName || !nextName.trim()) return;

    try {
      setSaving(true);

      await updateDoc(doc(db, "inventoryCategories", category.id), {
        name: nextName.trim(),
        updatedAt: serverTimestamp(),
      });

      await onSaved();
    } catch (err) {
      console.error("Failed to rename category:", err);
      alert("Failed to rename category.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={styles.modalOverlay}>
      <div style={{ ...styles.modal, maxWidth: 760 }}>
        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>Manage Categories</h2>
            <p style={styles.subtitle}>
              Add, rename, or disable inventory categories.
            </p>
          </div>

          <button onClick={onClose} style={styles.closeButton}>
            Close
          </button>
        </div>

        <div style={{ marginTop: 18, display: "flex", gap: 10 }}>
          <input
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            placeholder="New category name"
            style={styles.input}
          />

          <button
            onClick={handleAddCategory}
            disabled={saving}
            style={styles.primaryButton}
          >
            Add
          </button>
        </div>

        <div style={{ marginTop: 20 }}>
          {categories.map((category) => (
            <div key={category.id} style={styles.categoryRow}>
              <div>
                <strong>{category.name}</strong>
                <div style={{ fontSize: 12, color: "#64748b" }}>
                  {itemCountForCategory(category.name)} item(s)
                </div>
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button
                  style={styles.smallButton}
                  onClick={() => handleRenameCategory(category)}
                  disabled={saving}
                >
                  Rename
                </button>

                <button
                  style={styles.smallDangerButton}
                  onClick={() => handleDisableCategory(category)}
                  disabled={saving}
                >
                  Disable
                </button>
              </div>
            </div>
          ))}

          {categories.length === 0 && (
            <p style={{ color: "#64748b" }}>No active categories found.</p>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    padding: 24,
    background:
      "linear-gradient(120deg, rgba(238,231,255,1) 0%, rgba(239,246,255,1) 50%, rgba(220,252,255,1) 100%)",
    color: "#020617",
  },
  header: {
    maxWidth: 1180,
    margin: "0 auto 22px",
    background: "#0f172a",
    color: "#fff",
    borderRadius: 22,
    padding: 24,
    display: "flex",
    justifyContent: "space-between",
    gap: 20,
    flexWrap: "wrap",
  },
  title: {
    margin: 0,
    fontSize: 34,
    fontWeight: 800,
  },
  subtitle: {
    marginTop: 6,
    color: "#64748b",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(90px, 1fr))",
    gap: 10,
  },
  summaryBox: {
    border: "1px solid",
    borderRadius: 16,
    padding: "12px 16px",
    minWidth: 95,
    textAlign: "center",
  },
  summaryCount: {
    fontSize: 28,
    fontWeight: 900,
  },
  summaryLabel: {
    fontSize: 12,
    fontWeight: 800,
    textTransform: "uppercase",
  },
  tabs: {
    maxWidth: 1180,
    margin: "0 auto 22px",
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  tab: {
    border: "none",
    borderRadius: 14,
    padding: "12px 16px",
    fontWeight: 800,
    background: "#fff",
    color: "#334155",
    cursor: "pointer",
    boxShadow: "0 2px 8px rgba(15,23,42,0.08)",
  },
  tabActive: {
    background: "#0f172a",
    color: "#fff",
  },
  dashboard: {
    maxWidth: 1180,
    margin: "0 auto",
  },
  dashboardActions: {
  display: "flex",
  justifyContent: "flex-end",
  marginBottom: 16,
},
  section: {
    marginBottom: 26,
  },
  sectionTitle: {
    margin: "0 0 12px",
    fontSize: 22,
  },
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 16,
  },
  card: {
    border: "1px solid #e2e8f0",
    borderRadius: 18,
    background: "#fff",
    padding: 18,
    cursor: "pointer",
    textAlign: "left",
    boxShadow: "0 2px 10px rgba(15,23,42,0.06)",
  },
  cardTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    alignItems: "flex-start",
  },
  cardTitle: {
    margin: 0,
    fontSize: 21,
  },
  cardType: {
    margin: "5px 0 0",
    color: "#64748b",
    fontWeight: 700,
    textTransform: "capitalize",
  },
  badge: {
    border: "1px solid",
    borderRadius: 999,
    padding: "6px 10px",
    fontSize: 12,
    fontWeight: 900,
    whiteSpace: "nowrap",
  },
  cardBody: {
    marginTop: 14,
    color: "#334155",
    fontSize: 14,
  },
  placeholder: {
    maxWidth: 1180,
    margin: "0 auto",
    background: "#fff",
    borderRadius: 20,
    padding: 24,
    boxShadow: "0 2px 10px rgba(15,23,42,0.06)",
  },
  placeholderBox: {
    marginTop: 18,
    border: "2px dashed #cbd5e1",
    borderRadius: 16,
    padding: 40,
    textAlign: "center",
    color: "#64748b",
    background: "#f8fafc",
  },
  modalOverlay: {
  position: "fixed",
  inset: 0,
  background: "rgba(15,23,42,0.55)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: 20,
  zIndex: 9999,
},
  modal: {
  width: "100%",
  maxWidth: 720,
  maxHeight: "90vh",
  overflowY: "auto",
  background: "#fff",
  borderRadius: 22,
  padding: 24,
  boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
},
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "flex-start",
  },
  modalTitle: {
    margin: 0,
    fontSize: 28,
  },
  closeButton: {
    border: "1px solid #cbd5e1",
    borderRadius: 12,
    background: "#fff",
    padding: "9px 12px",
    fontWeight: 800,
    cursor: "pointer",
  },
  detailGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    marginTop: 20,
  },
  detailBox: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 16,
    padding: 14,
  },
  detailLabel: {
    fontSize: 12,
    fontWeight: 900,
    color: "#64748b",
    textTransform: "uppercase",
  },
  detailValue: {
    marginTop: 6,
    fontWeight: 900,
  },
  missingBox: {
    marginTop: 20,
    border: "1px solid #e2e8f0",
    borderRadius: 16,
    padding: 16,
  },
  missingRow: {
    display: "flex",
    justifyContent: "space-between",
    borderTop: "1px solid #e2e8f0",
    padding: "12px 0",
  },
  modalActions: {
    marginTop: 20,
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
  },
  secondaryButton: {
    border: "1px solid #cbd5e1",
    borderRadius: 12,
    background: "#fff",
    padding: "10px 14px",
    fontWeight: 800,
    cursor: "pointer",
  },
  primaryButton: {
    border: "none",
    borderRadius: 12,
    background: "#15803d",
    color: "#fff",
    padding: "10px 14px",
    fontWeight: 800,
    cursor: "pointer",
  },
  issueRow: {
  display: "flex",
  alignItems: "center",
  gap: 12,
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  padding: 12,
  marginBottom: 10,
},

qtyInput: {
  width: 80,
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  padding: 8,
},

table: {
  width: "100%",
  borderCollapse: "collapse",
  marginTop: 20,
},

th: {
  textAlign: "left",
  padding: "8px 10px",
  borderBottom: "1px solid #cbd5e1",
  fontSize: 12,
  color: "#475569",
  whiteSpace: "nowrap",
},

td: {
  padding: "9px 10px",
  borderBottom: "1px solid #e2e8f0",
  fontSize: 13,
  verticalAlign: "top",
},

fieldLabel: {
  display: "block",
  marginTop: 14,
  marginBottom: 6,
  fontWeight: 800,
  color: "#334155",
},

select: {
  width: "100%",
  border: "1px solid #cbd5e1",
  borderRadius: 12,
  padding: 12,
  fontSize: 16,
},

input: {
  width: "100%",
  border: "1px solid #cbd5e1",
  borderRadius: 12,
  padding: 12,
  fontSize: 16,
},

smallButton: {
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  background: "#fff",
  padding: "5px 8px",
  fontSize: 12,
  fontWeight: 800,
  cursor: "pointer",
},

smallDangerButton: {
  border: "1px solid #fecaca",
  borderRadius: 8,
  background: "#fee2e2",
  color: "#991b1b",
  padding: "5px 8px",
  fontSize: 12,
  fontWeight: 800,
  cursor: "pointer",
},

filterBar: {
  display: "grid",
  gridTemplateColumns: "1fr 180px 180px",
  gap: 10,
  margin: "12px 0 14px",
  alignItems: "center",
},

searchInput: {
  padding: "9px 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  fontSize: 14,
},

filterSelect: {
  padding: "9px 10px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  fontSize: 14,
  background: "#fff",
},

panel: {
  maxWidth: 1180,
  margin: "0 auto",
  background: "#fff",
  borderRadius: 20,
  padding: 24,
  boxShadow: "0 2px 10px rgba(15,23,42,0.06)",
},

panelHeader: {
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  alignItems: "flex-start",
  marginBottom: 14,
},

panelTitle: {
  margin: 0,
  fontSize: 26,
  fontWeight: 900,
},

requisitionLayout: {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1.5fr) minmax(340px, 0.9fr)",
  gap: 18,
  alignItems: "start",
},

requisitionItems: {
  minWidth: 0,
},

requisitionCart: {
  border: "1px solid #e2e8f0",
  borderRadius: 18,
  padding: 16,
  background: "#f8fafc",
},

supplierGroup: {
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: 14,
  padding: 12,
  marginBottom: 12,
},

cartRow: {
  display: "flex",
  alignItems: "center",
  gap: 10,
  borderTop: "1px solid #e2e8f0",
  padding: "10px 0",
},

cartTotal: {
  display: "flex",
  justifyContent: "space-between",
  fontSize: 18,
  padding: "14px 0",
  borderTop: "2px solid #cbd5e1",
},

textarea: {
  width: "100%",
  minHeight: 90,
  border: "1px solid #cbd5e1",
  borderRadius: 12,
  padding: 12,
  fontSize: 14,
  resize: "vertical",
},

requisitionFooter: {
  marginTop: 18,
  padding: 16,
  border: "1px solid #e2e8f0",
  borderRadius: 16,
  background: "#f8fafc",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
},

importButton: {
  background: "#1d4ed8",
  color: "#fff",
  padding: "12px 18px",
  borderRadius: 10,
  fontWeight: 700,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
},

dashboardTable: {
  display: "flex",
  flexDirection: "column",
  gap: 10,
},

dashboardHeaderRow: {
  display: "grid",
  gridTemplateColumns:
    "2fr 1fr 1fr 1fr 1fr 1fr 1.5fr",
  gap: 12,
  padding: "0 14px",
  fontSize: 12,
  fontWeight: 900,
  color: "#475569",
  textTransform: "uppercase",
},

dashboardRow: {
  display: "grid",
  gridTemplateColumns:
    "2fr 1fr 1fr 1fr 1fr 1fr 1.5fr",
  gap: 12,
  alignItems: "center",
  borderRadius: 18,
  border: "1px solid #dbe4ee",
  padding: 16,
  cursor: "pointer",
  transition: "0.2s ease",
  boxShadow: "0 2px 6px rgba(15,23,42,0.05)",
},

returnButton: {
  marginTop: 12,
  border: "1px solid rgba(255,255,255,0.35)",
  borderRadius: 12,
  background: "rgba(255,255,255,0.12)",
  color: "#fff",
  padding: "10px 14px",
  fontWeight: 900,
  cursor: "pointer",
},

categoryRow: {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  padding: 12,
  marginBottom: 10,
  background: "#f8fafc",
},
};